import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import lamejs from "@breezystack/lamejs";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import {
  XMarkIcon,
  PaperAirplaneIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button, Badge, Spinner } from "components/ui";
import { Input, Textarea } from "components/ui/Form";
import { toast } from "sonner";
import { sendWhatsAppMessage, sendWhatsAppMessageToMultiple, getWhatsAppTemplates, getApprovedWhatsAppTemplatesForMessaging, uploadWhatsAppMedia, sendWhatsAppMedia } from "services/whatsappTemplateService";
import { extractTemplateVariables, htmlToPlainText } from "services/templateService";
import WhatsAppTemplateMessageModal from "./WhatsAppTemplateMessageModal";
import { MediaPreviewModal } from "./MediaPreviewModal";
import LeadChipsInput from "./LeadChipsInput";
import UploadedFilesList from "./UploadedFilesList";
import VoiceRecordingsList from "./VoiceRecordingsList";
import MediaAttachmentButton from "./MediaAttachmentButton";
import VoiceRecordingControls from "./VoiceRecordingControls";
import { formatTime } from "../../../utils/dateUtils";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { formatNumber } from "utils/formatNumber";


const WhatsAppMessageModal = ({ isOpen, onClose, leadData, selectedLeads = [], isBulkMode = false }) => {
  const [message, setMessage] = useState("")
  const [selectedTab, setSelectedTab] = useState("compose") // "compose" or "template"
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [templateVariables, setTemplateVariables] = useState({})
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [templateSearch, setTemplateSearch] = useState("")
  const [isSending, setIsSending] = useState(false)
  const [showTemplateMessageModal, setShowTemplateMessageModal] = useState(false)
  const [selectedTemplateForSending, setSelectedTemplateForSending] = useState(null)

  // Media upload states
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [showMediaOptions, setShowMediaOptions] = useState(false)
  const [showMediaPreview, setShowMediaPreview] = useState(false)
  const [pendingFiles, setPendingFiles] = useState([])
  const [editingFileIndex, setEditingFileIndex] = useState(null)
  const [isUploadingMedia, setIsUploadingMedia] = useState(false)

  // Voice recording states
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [recordedAudios, setRecordedAudios] = useState([]) // Support multiple recordings

  // WhatsApp form data
  const [whatsappData, setWhatsappData] = useState({
    to: "",
    toLeads: [], // For bulk mode - array of lead objects with name, number, and id
    message: ""
  })

  const inputRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const recordingIntervalRef = useRef(null)
  const recordingTimeRef = useRef(0)
  const templatesLoadedRef = useRef(false)
  const dataInitializedRef = useRef(false)
  const mediaOptionsRef = useRef(null)
  const scrollContainerRef = useRef(null)

  // Memoize valid leads to prevent infinite re-renders
  // Include ALL leads, even those without phone numbers
  const validLeads = useMemo(() => {
    return isBulkMode ? selectedLeads : []
  }, [isBulkMode, selectedLeads])

  const getMobileFromLead = useCallback((lead) => {
    let mobile = lead?.mobile || lead?.Mobile || lead?.phone || lead?.PhoneNumber || lead?.cellPhone || lead?.Phone;
    if (!mobile && lead?.leadinfo) {
      try {
        const parsed = typeof lead.leadinfo === 'string' ? JSON.parse(lead.leadinfo) : lead.leadinfo;
        mobile = parsed?.PhoneNumber || parsed?.mobile || parsed?.phone;
      } catch (e) { }
    }
    return mobile;
  }, []);

  const getNameFromLead = useCallback((lead) => {
    let name = lead?.name || lead?.leadname || lead?.leadName || lead?.Name || lead?.FullName || lead?.clientContact || lead?.title;
    if (!name && lead?.leadinfo) {
      try {
        const parsed = typeof lead.leadinfo === 'string' ? JSON.parse(lead.leadinfo) : lead.leadinfo;
        name = parsed?.FullName || parsed?.Name || parsed?.name || parsed?.leadName || parsed?.clientContact;
      } catch (e) { }
    }
    return name || 'Unknown Contact';
  }, []);

  // Load templates function
  const loadTemplates = useCallback(async (searchTerm = "") => {
    try {
      setTemplatesLoading(true)
      const result = await getApprovedWhatsAppTemplatesForMessaging({
        isActive: true,
        pageNumber: 1,
        pageSize: 50,
        search: searchTerm
      })
      if (result.success) {
        setTemplates(result.data)
      } else {
        toast.error(result.error || "Failed to load templates")
      }
    } catch (error) {
      console.error("Error loading templates:", error)
      toast.error("Failed to load templates")
    } finally {
      setTemplatesLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      // Only load templates if not already loaded or modal was closed and reopened
      if (!templatesLoadedRef.current) {
        loadTemplates()
        templatesLoadedRef.current = true
      }
      inputRef.current?.focus()
    } else {
      // Reset flags when modal closes
      templatesLoadedRef.current = false
      dataInitializedRef.current = false

      if (uploadedFiles.length) {
        uploadedFiles.forEach(file => {
          if (file.preview) {
            URL.revokeObjectURL(file.preview)
          }
        })
        setUploadedFiles([])
      }

      if (pendingFiles.length) {
        setPendingFiles([])
      }

      setShowMediaPreview(false)
      setEditingFileIndex(null)

      // Clean up audio recordings
      if (recordedAudios.length > 0) {
        recordedAudios.forEach(audio => {
          if (audio.url) {
            URL.revokeObjectURL(audio.url)
          }
        })
        setRecordedAudios([])
      }

      setIsRecording(false)
      setIsPaused(false)
      setRecordingTime(0)
    }
  }, [isOpen, loadTemplates, uploadedFiles, pendingFiles, recordedAudios])

  // Separate useEffect for form initialization to avoid infinite loops
  useEffect(() => {
    if (isOpen && !dataInitializedRef.current) {
      // Reset form when modal opens (only once per modal open)
      const initialToValue = isBulkMode
        ? `${validLeads.length} recipients selected`
        : getMobileFromLead(leadData) || ""

      const initialToLeads = isBulkMode
        ? validLeads.map(lead => {
          const phoneNumber = getMobileFromLead(lead)
          const hasValidPhone = phoneNumber && phoneNumber !== "N/A" && String(phoneNumber).trim() !== ""

          return {
            id: lead.id || lead.leadId || lead.LeadId || lead.userId,
            number: hasValidPhone ? phoneNumber : "",
            name: getNameFromLead(lead),
            hasPhone: hasValidPhone
          }
        })
        : []

      setWhatsappData(prev => ({
        ...prev,
        to: initialToValue,
        toLeads: initialToLeads,
        message: ""
      }))

      dataInitializedRef.current = true
    }
  }, [isOpen, isBulkMode, validLeads, leadData])

  // Replace variables only if they have values, otherwise keep the placeholder
  const replaceVariablesSelectively = useCallback((text, variables) => {
    if (!text) return text

    let result = text
    Object.keys(variables).forEach(varName => {
      const value = variables[varName]
      // Only replace if value is not empty
      if (value && value.trim() !== '') {
        const regex = new RegExp(`\\{\\{${varName}\\}\\}`, 'gi')
        result = result.replace(regex, value)
      }
      // If value is empty, keep the {{variable}} placeholder as-is
    })

    return result
  }, [])

  useEffect(() => {
    if (selectedTemplate) {
      // WhatsApp templates use BodyText instead of Body
      const templateBody = selectedTemplate.BodyText || selectedTemplate.Body || ''
      const variables = extractTemplateVariables(templateBody)
      const variableDefaults = {}
      variables.forEach(variable => {
        // Set default values based on lead data
        switch (variable.toLowerCase()) {
          case 'name':
          case 'leadname':
          case 'fullname':
            variableDefaults[variable] = leadData?.name || ''
            break
          case 'email':
            variableDefaults[variable] = leadData?.email || ''
            break
          case 'mobile':
          case 'phone':
          case 'phonenumber':
            variableDefaults[variable] = leadData?.mobile || leadData?.phone || ''
            break
          case 'company':
            variableDefaults[variable] = leadData?.company || ''
            break
          default:
            variableDefaults[variable] = ''
        }
      })
      setTemplateVariables(variableDefaults)

      // Set the message to the processed template (only replace variables with values)
      const processedTemplate = htmlToPlainText(replaceVariablesSelectively(templateBody, variableDefaults))
      setMessage(processedTemplate)
      setWhatsappData(prev => ({
        ...prev,
        message: processedTemplate
      }))
    }
  }, [selectedTemplate, leadData, replaceVariablesSelectively])

  const handleTemplateSearch = (searchTerm) => {
    setTemplateSearch(searchTerm)
    // Clear existing timeout
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout)
    }
    // Set new timeout
    window.searchTimeout = setTimeout(() => {
      loadTemplates(searchTerm)
      templatesLoadedRef.current = true // Mark as loaded after search
    }, 300)
  }

  const handleTemplateSelect = (template) => {
    // Open the new template message modal with variable editing and preview
    setSelectedTemplateForSending(template)
    setShowTemplateMessageModal(true)
  }

  const handleVariableChange = (variable, value) => {
    const updatedVariables = { ...templateVariables, [variable]: value }
    setTemplateVariables(updatedVariables)

    if (selectedTemplate) {
      // WhatsApp templates use BodyText instead of Body
      const templateBody = selectedTemplate.BodyText || selectedTemplate.Body || ''
      const processedTemplate = htmlToPlainText(replaceVariablesSelectively(templateBody, updatedVariables))
      setMessage(processedTemplate)
      setWhatsappData(prev => ({
        ...prev,
        message: processedTemplate
      }))
    }
  }

  const handleSendMessage = async () => {
    // Check if we have either text message or media files
    const hasTextMessage = message.trim().length > 0
    const hasMediaFiles = uploadedFiles.length > 0 || recordedAudios.length > 0

    if (!hasTextMessage && !hasMediaFiles) {
      toast.error("Please enter a message or attach media")
      return
    }

    if (isBulkMode) {
      // Bulk WhatsApp mode
      if (whatsappData.toLeads.length === 0) {
        toast.error("No phone numbers selected for WhatsApp")
        return
      }

      try {
        setIsSending(true)

        // Transform all lead chips to format for API
        // Backend will handle fetching phone numbers from DB
        const leadsForWhatsApp = whatsappData.toLeads.map((lead) => ({
          id: lead.id,
          mobile: lead.number || null, // Send null if no number, backend will fetch it
          name: lead.name
        }))

        // Send text message if present
        if (hasTextMessage) {
          const result = await sendWhatsAppMessageToMultiple(leadsForWhatsApp, message)

          if (!result.success) {
            throw new Error(result.message || "Failed to send WhatsApp messages")
          }
        }

        // Send media if present
        if (hasMediaFiles) {
          // Prepare leads object (dictionary format: leadId -> phoneNumber)
          const leadsMap = {}
          whatsappData.toLeads.forEach(lead => {
            leadsMap[lead.id] = lead.number || ""
          })

          // Prepare whatsappMedias array from uploaded files
          const whatsappMedias = []

          // Add uploaded media files
          uploadedFiles.forEach(file => {
            if (file.mediaId && file.mediaType) {
              whatsappMedias.push({
                mediaId: file.mediaId,
                mediaType: file.mediaType,
                caption: file.caption || ""
              })
            }
          })

          // Add recorded audio files (if they have been uploaded)
          recordedAudios.forEach(audio => {
            if (audio.mediaId && audio.mediaType) {
              whatsappMedias.push({
                mediaId: audio.mediaId,
                mediaType: audio.mediaType,
                caption: audio.name || ""
              })
            }
          })

          if (whatsappMedias.length > 0) {
            const mediaResult = await sendWhatsAppMedia(leadsMap, whatsappMedias)

            if (!mediaResult.success) {
              throw new Error(mediaResult.message || "Failed to send WhatsApp media")
            }
          }
        }

        toast.success(`WhatsApp sent to ${whatsappData.toLeads.length} leads successfully!`)
        setMessage("")
        setSelectedTemplate(null)
        setTemplateVariables({})
        setWhatsappData({
          to: "",
          toLeads: [],
          message: ""
        })
        setUploadedFiles([])
        setRecordedAudios([])
        onClose()
      } catch (error) {
        console.error("Error sending bulk WhatsApp:", error)
        toast.error(error.message || "Failed to send WhatsApp messages")
      } finally {
        setIsSending(false)
      }
    } else {
      // Single WhatsApp mode
      if (!leadData?.mobile && !leadData?.Mobile && !leadData?.phone && !leadData?.PhoneNumber) {
        toast.error("Lead phone number is required to send WhatsApp")
        return
      }

      if (!leadData?.id && !leadData?.leadId && !leadData?.LeadId) {
        toast.error("Lead ID is required to send WhatsApp")
        return
      }

      try {
        setIsSending(true)

        const leadId = leadData.id || leadData.leadId || leadData.LeadId
        const phoneNumber = leadData.mobile || leadData.Mobile || leadData.phone || leadData.PhoneNumber

        // Send text message if present
        if (hasTextMessage) {
          const result = await sendWhatsAppMessage(leadId, phoneNumber, message)

          if (!result.success) {
            throw new Error(result.message || "Failed to send WhatsApp")
          }
        }

        // Send media if present
        if (hasMediaFiles) {
          // Prepare leads object (dictionary format: leadId -> phoneNumber)
          const leadsMap = {
            [leadId]: phoneNumber
          }

          // Prepare whatsappMedias array from uploaded files
          const whatsappMedias = []

          // Add uploaded media files
          uploadedFiles.forEach(file => {
            if (file.mediaId && file.mediaType) {
              whatsappMedias.push({
                mediaId: file.mediaId,
                mediaType: file.mediaType,
                caption: file.caption || ""
              })
            }
          })

          // Add recorded audio files (if they have been uploaded)
          recordedAudios.forEach(audio => {
            if (audio.mediaId && audio.mediaType) {
              whatsappMedias.push({
                mediaId: audio.mediaId,
                mediaType: audio.mediaType,
                caption: audio.name || ""
              })
            }
          })

          if (whatsappMedias.length > 0) {
            const mediaResult = await sendWhatsAppMedia(leadsMap, whatsappMedias)

            if (!mediaResult.success) {
              throw new Error(mediaResult.message || "Failed to send WhatsApp media")
            }
          }
        }

        toast.success("WhatsApp sent successfully!")
        setMessage("")
        setSelectedTemplate(null)
        setTemplateVariables({})
        setWhatsappData({
          to: "",
          toLeads: [],
          message: ""
        })
        setUploadedFiles([])
        setRecordedAudios([])
        onClose()
      } catch (error) {
        console.error("Error sending WhatsApp:", error)
        toast.error(error.message || "Failed to send WhatsApp")
      } finally {
        setIsSending(false)
      }
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Media Upload Handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    // Allow multiple uploads of the same file (user may want to crop/trim differently)
    // Generate unique IDs using timestamp and random numbers
    const newFiles = files.map(file => {
      const isMediaFile = file.type.startsWith('image/') || file.type.startsWith('video/')
      const previewUrl = isMediaFile ? URL.createObjectURL(file) : null

      return {
        file,
        id: `${Date.now()}_${Math.random()}_${Math.floor(Math.random() * 100000)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: previewUrl,
        originalPreview: previewUrl, // Store original preview for cropping
        caption: ""
      }
    })

    if (newFiles.length === 0) {
      e.target.value = "" // Reset file input
      return
    }

    setPendingFiles(newFiles)
    setEditingFileIndex(null)
    setShowMediaPreview(true)
    setShowMediaOptions(false)

    // Reset file input to allow selecting the same file again
    e.target.value = ""
  }

  const handleMediaConfirm = async (filesWithCaptions) => {
    if (!filesWithCaptions || filesWithCaptions.length === 0) {
      setShowMediaPreview(false)
      setPendingFiles([])
      return
    }

    // STEP 1: Immediately process and show files in UI (without mediaId yet)
    const processedFiles = filesWithCaptions.map((file, index) => {
      // Get the actual file object (could be croppedFile, trimmedFile, or original file)
      const actualFile = file.croppedFile || file.trimmedFile || file.file || file.originalFile || file

      // Ensure all required properties are present
      return {
        id: file.id || `${Date.now()}_${Math.random()}_${index}`,
        file: actualFile,
        originalFile: file.originalFile || file.file || actualFile,
        name: file.name || actualFile.name || 'Unnamed file',
        size: file.size || actualFile.size || 0,
        type: file.type || actualFile.type || '',
        preview: file.preview || null,
        originalPreview: file.originalPreview || file.preview || null,
        caption: file.caption || '',
        croppedFile: file.croppedFile || null,
        trimmedFile: file.trimmedFile || null,
        // persist trim metadata for subsequent edits
        lastTrimState: file.lastTrimState || (typeof file.trimStart === 'number' && typeof file.trimEnd === 'number'
          ? { trimStart: file.trimStart, trimEnd: file.trimEnd, trimDuration: (file.trimEnd - file.trimStart) }
          : null),
        trimStart: typeof file.trimStart === 'number' ? file.trimStart : 0,
        trimEnd: typeof file.trimEnd === 'number' ? file.trimEnd : 0,
        trimDuration: typeof file.trimDuration === 'number' ? file.trimDuration : (typeof file.trimStart === 'number' && typeof file.trimEnd === 'number' ? (file.trimEnd - file.trimStart) : 0),
        mediaId: null, // Will be updated after API upload
        mediaType: null, // Will be updated after API upload
        isUploading: true // Flag to show uploading state
      }
    })

    // STEP 2: Immediately add files to UI
    if (editingFileIndex !== null) {
      // When editing, replace the file at editingFileIndex with the updated file(s)
      setUploadedFiles(prev => {
        const updated = [...prev]

        // Clean up old preview URL if exists
        if (updated[editingFileIndex].preview) {
          URL.revokeObjectURL(updated[editingFileIndex].preview)
        }

        // If no files returned (deleted), remove the file at editingFileIndex
        if (processedFiles.length === 0) {
          updated.splice(editingFileIndex, 1)
        } else {
          // Replace the edited file with the updated one
          updated[editingFileIndex] = processedFiles[0]
        }

        return updated
      })
      setEditingFileIndex(null)
    } else {
      // Add new files
      setUploadedFiles(prev => [...prev, ...processedFiles])
    }

    // Reset modal states immediately
    setShowMediaPreview(false)
    setPendingFiles([])

    // STEP 3: Upload files to API in background/parallel
    try {
      setIsUploadingMedia(true)

      const uploadResult = await uploadWhatsAppMedia(filesWithCaptions)

      if (!uploadResult.success) {
        throw new Error(uploadResult.message || "Failed to upload media")
      }

      // STEP 4: Update files with mediaId and mediaType from API response
      // uploadResult.data is an array of { mediaId, mediaType }
      setUploadedFiles(prev => {
        return prev.map(file => {
          // Find the matching file by id
          const matchingFileIndex = processedFiles.findIndex(pf => pf.id === file.id)

          if (matchingFileIndex !== -1 && uploadResult.data && uploadResult.data[matchingFileIndex]) {
            const mediaInfo = uploadResult.data[matchingFileIndex]
            return {
              ...file,
              mediaId: mediaInfo.mediaId,
              mediaType: mediaInfo.mediaType,
              isUploading: false
            }
          }

          return file
        })
      })

      if (editingFileIndex !== null) {
        toast.success("File updated and uploaded successfully")
      } else {
        toast.success(`${processedFiles.length} file(s) uploaded successfully`)
      }
    } catch (error) {
      console.error('Error uploading media:', error)
      toast.error(error.message || 'Failed to upload media')

      // Mark files as failed upload
      setUploadedFiles(prev => {
        return prev.map(file => {
          const matchingFile = processedFiles.find(pf => pf.id === file.id)
          if (matchingFile) {
            return {
              ...file,
              isUploading: false,
              uploadFailed: true
            }
          }
          return file
        })
      })
    } finally {
      setIsUploadingMedia(false)
    }
  }

  const handleMediaCancel = () => {
    setShowMediaPreview(false)
    setPendingFiles([])
    setEditingFileIndex(null)
  }

  const handleEditFile = (index) => {
    const fileToEdit = uploadedFiles[index]
    // Ensure the file has all necessary properties for editing
    const editableFile = {
      ...fileToEdit,
      file: fileToEdit.file || fileToEdit.croppedFile || fileToEdit,
      preview: fileToEdit.preview,
      originalPreview: fileToEdit.originalPreview || fileToEdit.preview, // Preserve original for re-cropping
      name: fileToEdit.name,
      size: fileToEdit.size,
      type: fileToEdit.type,
      caption: fileToEdit.caption || "",
      id: fileToEdit.id
    }
    setPendingFiles([editableFile])
    setEditingFileIndex(index)
    setShowMediaPreview(true)
  }


  const removeFile = (fileId) => {
    setUploadedFiles(prev => {
      const file = prev.find(f => f.id === fileId)
      if (file?.preview) {
        URL.revokeObjectURL(file.preview)
      }
      return prev.filter(f => f.id !== fileId)
    })
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  // Helper function to convert audio blob to MP3 using lamejs
  const convertToMp3 = async (audioBlob) => {
    try {
      // Decode the audio blob to get raw PCM data
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const arrayBuffer = await audioBlob.arrayBuffer()
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)

      // Get audio data
      const numberOfChannels = audioBuffer.numberOfChannels
      const sampleRate = audioBuffer.sampleRate
      const samples = audioBuffer.getChannelData(0) // Get first channel

      // Convert Float32Array to Int16Array for lamejs
      const int16Samples = new Int16Array(samples.length)
      for (let i = 0; i < samples.length; i++) {
        // Clamp and convert to 16-bit integer
        const s = Math.max(-1, Math.min(1, samples[i]))
        int16Samples[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
      }

      // Create MP3 encoder
      // Use mono (1 channel) for voice recordings to reduce file size
      const mp3Encoder = new lamejs.Mp3Encoder(1, sampleRate, 128) // 128 kbps

      // Encode in chunks
      const mp3Data = []
      const sampleBlockSize = 1152 // Must be a multiple of 576 for lamejs

      for (let i = 0; i < int16Samples.length; i += sampleBlockSize) {
        const sampleChunk = int16Samples.subarray(i, i + sampleBlockSize)
        const mp3buf = mp3Encoder.encodeBuffer(sampleChunk)
        if (mp3buf.length > 0) {
          mp3Data.push(mp3buf)
        }
      }

      // Flush remaining data
      const mp3buf = mp3Encoder.flush()
      if (mp3buf.length > 0) {
        mp3Data.push(mp3buf)
      }

      // Close audio context
      await audioContext.close()

      // Create MP3 blob
      const mp3Blob = new Blob(mp3Data, { type: 'audio/mpeg' })
      return mp3Blob
    } catch (error) {
      console.error('Error converting to MP3:', error)
      throw error
    }
  }

  // Voice Recording Handlers
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // Use a format that browsers can decode for conversion to MP3
      // We'll record in a browser-supported format and convert to MP3 after
      let mimeType = 'audio/webm' // Most widely supported for recording

      const supportedTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg',
        'audio/mp4',
      ]

      for (const type of supportedTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type
          break
        }
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = async () => {
        // Build blob from recorded chunks
        const recordedBlob = new Blob(audioChunksRef.current, { type: mimeType })

        // Add new recording to the array immediately (with uploading/converting state)
        const recordingId = `audio_${Date.now()}_${Math.random()}`
        let recordingCount = 0

        // Create temporary URL for preview (will be replaced with MP3 URL)
        const tempUrl = URL.createObjectURL(recordedBlob)

        setRecordedAudios(prev => {
          recordingCount = prev.length + 1
          const newRecording = {
            id: recordingId,
            blob: recordedBlob,
            file: null,
            url: tempUrl,
            duration: recordingTimeRef.current,
            timestamp: formatTime(new Date()),
            name: `Voice Recording ${recordingCount}`,
            isUploading: true,
            isConverting: true,
            mediaId: null,
            mediaType: null
          }
          return [...prev, newRecording]
        })

        stream.getTracks().forEach(track => track.stop())

        // Convert to MP3 and upload
        try {
          // Convert recorded audio to MP3
          const mp3Blob = await convertToMp3(recordedBlob)
          const mp3Url = URL.createObjectURL(mp3Blob)

          // Create MP3 file for upload
          const mp3File = new File(
            [mp3Blob],
            `voice_recording_${Date.now()}.mp3`,
            { type: 'audio/mpeg' }
          )

          // Update recording with MP3 data and mark conversion complete
          setRecordedAudios(prev => prev.map(recording =>
            recording.id === recordingId
              ? {
                ...recording,
                blob: mp3Blob,
                file: mp3File,
                url: mp3Url,
                isConverting: false
              }
              : recording
          ))

          // Revoke the temporary URL
          URL.revokeObjectURL(tempUrl)

          // Upload MP3 file
          const uploadResult = await uploadWhatsAppMedia([{
            file: mp3File,
            name: `Voice Recording ${recordingCount}.mp3`,
            type: 'audio/mpeg'
          }])

          if (uploadResult.success && uploadResult.data && uploadResult.data[0]) {
            // Update recording with mediaId and mediaType
            setRecordedAudios(prev => prev.map(recording =>
              recording.id === recordingId
                ? {
                  ...recording,
                  mediaId: uploadResult.data[0].mediaId,
                  mediaType: uploadResult.data[0].mediaType,
                  isUploading: false
                }
                : recording
            ))
          } else {
            throw new Error("Failed to upload audio")
          }
        } catch (error) {
          console.error('Error converting/uploading voice recording:', error)
          // Mark recording as failed
          setRecordedAudios(prev => prev.map(recording =>
            recording.id === recordingId
              ? { ...recording, isUploading: false, isConverting: false, uploadFailed: true }
              : recording
          ))
          toast.error('Failed to process voice recording')
        }
      }

      mediaRecorder.start()
      setIsRecording(true)
      setRecordingTime(0)
      recordingTimeRef.current = 0

      recordingIntervalRef.current = setInterval(() => {
        recordingTimeRef.current += 1
        setRecordingTime(recordingTimeRef.current)
      }, 1000)

      toast.success("Recording started")
    } catch (error) {
      console.error("Error starting recording:", error)
      toast.error("Failed to start recording. Please check microphone permissions.")
    }
  }

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.pause()
      setIsPaused(true)
      clearInterval(recordingIntervalRef.current)
      toast.info("Recording paused")
    }
  }

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isPaused) {
      mediaRecorderRef.current.resume()
      setIsPaused(false)
      recordingIntervalRef.current = setInterval(() => {
        recordingTimeRef.current += 1
        setRecordingTime(recordingTimeRef.current)
      }, 1000)
      toast.info("Recording resumed")
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      // Save current scroll position before state update
      const scrollTop = scrollContainerRef.current?.scrollTop || 0

      mediaRecorderRef.current.stop()
      setIsRecording(false)
      setIsPaused(false)
      clearInterval(recordingIntervalRef.current)

      // Restore scroll position after state update
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollTop
        }
      })

      toast.success("Recording saved")
    }
  }

  const cancelRecording = () => {
    if (mediaRecorderRef.current) {
      const stream = mediaRecorderRef.current.stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop())
      }
      // Don't call stop() as it would save the recording
      mediaRecorderRef.current = null
    }
    audioChunksRef.current = []
    setIsRecording(false)
    setIsPaused(false)
    setRecordingTime(0)
    recordingTimeRef.current = 0
    clearInterval(recordingIntervalRef.current)
    toast.info("Recording cancelled")
  }

  const deleteAudioRecording = (audioId) => {
    setRecordedAudios(prev => {
      const audio = prev.find(a => a.id === audioId)
      if (audio?.url) {
        URL.revokeObjectURL(audio.url)
      }
      return prev.filter(a => a.id !== audioId)
    })
    toast.success("Recording deleted")
  }

  // Click outside to close media options
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mediaOptionsRef.current && !mediaOptionsRef.current.contains(event.target)) {
        setShowMediaOptions(false)
      }
    }

    if (showMediaOptions) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showMediaOptions])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup object URLs
      uploadedFiles.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview)
        }
      })
      recordedAudios.forEach(audio => {
        if (audio.url) {
          URL.revokeObjectURL(audio.url)
        }
      })
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current)
      }
    }
  }, [uploadedFiles, recordedAudios])

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClose={() => { }}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="relative w-full max-w-4xl transform overflow-hidden rounded-lg bg-white shadow-2xl transition-all dark:bg-gray-900 outline-none focus:outline-none">
              {/* Header */}
              <div className="bg-gradient-to-r from-[#075E54] to-[#128C7E] text-white p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <ChatBubbleLeftRightIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <DialogTitle as="h3" className="text-base font-medium text-white">
                      {isBulkMode
                        ? `Send WhatsApp to ${selectedLeads.length} Lead${selectedLeads.length > 1 ? 's' : ''}`
                        : `Send WhatsApp to ${leadData?.name || "Lead Contact"}`
                      }
                    </DialogTitle>
                    <p className="text-sm text-white/80">
                      {isBulkMode
                        ? `${selectedLeads.length} lead${selectedLeads.length > 1 ? 's' : ''} selected for WhatsApp`
                        : ((leadData?.mobile || leadData?.Mobile || leadData?.phone || leadData?.PhoneNumber) ? formatNumber(leadData.mobile || leadData.Mobile || leadData.phone || leadData.PhoneNumber) : "+91 98765 43210")
                      }
                    </p>
                  </div>
                </div>
                <Button
                  onClick={onClose}
                  variant="flat"
                  isIcon
                  className="w-10 h-10 rounded-full text-white hover:bg-white/10 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </Button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                <div className="flex px-4">
                  <button
                    onClick={() => setSelectedTab("compose")}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${selectedTab === "compose"
                      ? "border-[#128C7E] text-[#128C7E] dark:text-[#25D366]"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                      }`}
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    Compose Message
                  </button>
                  <button
                    onClick={() => setSelectedTab("template")}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${selectedTab === "template"
                      ? "border-[#128C7E] text-[#128C7E] dark:text-[#25D366]"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                      }`}
                  >
                    <DocumentTextIcon className="h-4 w-4" />
                    Choose Template
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div ref={scrollContainerRef} className="p-6 max-h-[65vh] overflow-y-auto custom-scrollbar bg-white dark:bg-gray-900">
                {selectedTab === "compose" && (
                  <div className="space-y-4">
                    {/* Recipients Section */}
                    {isBulkMode ? (
                      <LeadChipsInput
                        label="Recipients"
                        leads={whatsappData.toLeads}
                        onChange={(newLeads) => setWhatsappData(prev => ({ ...prev, toLeads: newLeads }))}
                        placeholder="Add phone number..."
                      />
                    ) : (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          To
                        </label>
                        <div className="p-3 border rounded-lg bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600">
                          <div className="flex items-center gap-2">
                            <PhoneIcon className="w-4 h-4 text-gray-500" />
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {leadData?.name || leadData?.leadname || leadData?.leadName || leadData?.Name || 'Unknown Contact'}
                              </span>
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {(leadData?.mobile || leadData?.Mobile || leadData?.phone || leadData?.PhoneNumber) ? formatNumber(leadData.mobile || leadData.Mobile || leadData.phone || leadData.PhoneNumber) : 'No phone number'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedTemplate && (
                      <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                        <div className="flex items-center gap-2 mb-3">
                          <DocumentTextIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                          <span className="text-sm font-semibold text-green-900 dark:text-green-100">
                            Template: {selectedTemplate.Name}
                          </span>
                          <Button
                            onClick={() => {
                              setSelectedTemplate(null)
                              setMessage("")
                              setTemplateVariables({})
                            }}
                            variant="flat"
                            size="sm"
                            className="ml-auto text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <XMarkIcon className="h-4 w-4 mr-1" />
                            Clear Template
                          </Button>
                        </div>

                        {/* Template Variables */}
                        {(() => {
                          const templateBody = selectedTemplate.BodyText || selectedTemplate.Body || ''
                          const variables = extractTemplateVariables(templateBody)
                          return variables.length > 0 && (
                            <div className="space-y-3 mb-4">
                              <div className="flex items-center gap-2 mb-2">
                                <PencilSquareIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                                  Fill in the template variables:
                                </p>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {variables.map((variable) => (
                                  <div key={variable} className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-green-200 dark:border-green-700">
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                      {`{{${variable}}}`}
                                    </label>
                                    <Input
                                      value={templateVariables[variable] || ''}
                                      onChange={(e) => handleVariableChange(variable, e.target.value)}
                                      placeholder={`Enter ${variable}`}
                                      className="text-sm"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        })()}

                        {/* Message Preview Label */}
                        <div className="flex items-center gap-2 mb-2 mt-4">
                          <ChatBubbleLeftRightIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                          <label className="text-sm font-medium text-green-800 dark:text-green-200">
                            Message Preview (Read-only)
                          </label>
                        </div>
                      </div>
                    )}

                    {!selectedTemplate && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Message
                        </label>
                      </div>
                    )}

                    <div>
                      <Textarea
                        ref={inputRef}
                        value={message}
                        onChange={(e) => {
                          if (!selectedTemplate) {
                            setMessage(e.target.value)
                            setWhatsappData(prev => ({ ...prev, message: e.target.value }))
                          }
                        }}
                        placeholder={selectedTemplate ? "This is a preview of your template with filled variables..." : "Type your message..."}
                        className={`min-h-[120px] max-h-[180px] resize-none ${selectedTemplate ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : ''}`}
                        rows={5}
                        readOnly={selectedTemplate !== null}
                      />

                      {/* Character Count */}
                      <div className="flex justify-between items-center mt-2">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          <div>{message.length} characters</div>
                          {isBulkMode && whatsappData.toLeads.length > 0 && (
                            <div className="text-xs">
                              {whatsappData.toLeads.length} recipient{whatsappData.toLeads.length > 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                        {selectedTemplate && (
                          <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                            Using Template
                          </div>
                        )}
                      </div>

                      {/* Media Upload & Voice Recording Section */}
                      {!selectedTemplate && (
                        <div className="mt-4 space-y-3">
                          {/* Uploaded Files Preview */}
                          <UploadedFilesList
                            files={uploadedFiles}
                            onEditFile={handleEditFile}
                            onRemoveFile={removeFile}
                          />

                          {/* Voice Recordings Preview */}
                          <VoiceRecordingsList
                            recordings={recordedAudios}
                            onDeleteRecording={deleteAudioRecording}
                          />

                          {/* Media Options & Voice Recording Controls */}
                          <div className="flex items-center gap-2 pt-3 mt-3 border-t border-gray-200 dark:border-gray-700">
                            {/* Media Upload Button */}
                            <MediaAttachmentButton
                              showMediaOptions={showMediaOptions}
                              onToggleOptions={setShowMediaOptions}
                              onFileSelect={handleFileSelect}
                              mediaOptionsRef={mediaOptionsRef}
                            />

                            {/* Voice Recording Controls */}
                            <VoiceRecordingControls
                              isRecording={isRecording}
                              isPaused={isPaused}
                              recordingTime={recordingTime}
                              onStartRecording={startRecording}
                              onPauseRecording={pauseRecording}
                              onResumeRecording={resumeRecording}
                              onStopRecording={stopRecording}
                              onCancelRecording={cancelRecording}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedTab === "template" && (
                  <div className="space-y-4">
                    {/* Search */}
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        value={templateSearch}
                        onChange={(e) => handleTemplateSearch(e.target.value)}
                        placeholder="Search templates..."
                        className="pl-10"
                      />
                    </div>

                    {/* Templates List */}
                    {templatesLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Spinner className="h-6 w-6" />
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading templates...</span>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
                        {templates.length === 0 ? (
                          <div className="text-center py-8">
                            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                              No templates found
                            </h3>
                            <p className="text-gray-500 dark:text-gray-400">
                              {templateSearch ? "Try a different search term" : "No WhatsApp templates available"}
                            </p>
                          </div>
                        ) : (
                          templates.map((template) => (
                            <div
                              key={template.TemplateId}
                              onClick={() => handleTemplateSelect(template)}
                              className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 hover:border-green-500 hover:bg-green-50 transition-all dark:border-gray-700 dark:bg-gray-800 dark:hover:border-green-400 dark:hover:bg-green-900/20"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                                    {template.Name}
                                  </h4>
                                  {template.Subject && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                      {template.Subject}
                                    </p>
                                  )}
                                  <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                                    {htmlToPlainText(template.BodyText || template.Body || '')}
                                  </p>

                                  {(() => {
                                    const templateBody = template.BodyText || template.Body || ''
                                    const variables = extractTemplateVariables(templateBody)
                                    return variables.length > 0 && (
                                      <div className="mt-2 flex flex-wrap gap-1">
                                        {variables.map((variable) => (
                                          <Badge key={variable} variant="soft" color="primary" className="text-xs">
                                            {`{{${variable}}}`}
                                          </Badge>
                                        ))}
                                      </div>
                                    )
                                  })()}
                                </div>
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleTemplateSelect(template)
                                  }}
                                  variant="outlined"
                                  size="sm"
                                  className="ml-3"
                                >
                                  Use Template
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-white dark:bg-gray-900 px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {isSending && (
                    <div className="flex items-center gap-2">
                      <Spinner className="w-4 h-4 text-[#25D366]" />
                      <span className="text-[#128C7E] dark:text-[#25D366]">Sending WhatsApp...</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <Button
                    onClick={onClose}
                    variant="outlined"
                    disabled={isSending}
                    className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Cancel
                  </Button>
                  <button
                    onClick={handleSendMessage}
                    disabled={isSending || (!message.trim() && uploadedFiles.length === 0 && recordedAudios.length === 0)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#128C7E] hover:bg-[#075E54] text-white rounded-lg transition-colors shadow-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSending ? (
                      <>
                        <Spinner className="w-4 h-4" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <PaperAirplaneIcon className="w-4 h-4" />
                        <span>Send WhatsApp</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>

      {/* Media Preview Modal with Cropping */}
      <MediaPreviewModal
        isOpen={showMediaPreview}
        onClose={handleMediaCancel}
        files={pendingFiles}
        onConfirm={handleMediaConfirm}
        initialIndex={0}
        isUploading={isUploadingMedia}
      />

      {/* WhatsApp Template Message Modal - Opens when template is selected */}
      <WhatsAppTemplateMessageModal
        isOpen={showTemplateMessageModal && selectedTemplateForSending !== null}
        onClose={() => {
          setShowTemplateMessageModal(false)
          setSelectedTemplateForSending(null)
          // Close the parent modal as well after sending
          onClose()
        }}
        template={selectedTemplateForSending}
        leadData={leadData}
        leads={validLeads}
        isBulkMode={isBulkMode}
      />
    </>
  )
}

export default WhatsAppMessageModal







